import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { GardforsaljningRegion } from "@/data/gardforsaljningRegions";

export interface GardforsaljningProducer {
  id: string;
  slug: string;
  name: string;
  city: string | null;
  region: string | null;
  county: string | null;
  short_description: string | null;
  updated_at: string;
  has_farm_shop: boolean;
}

/**
 * Fetches public producers with has_farm_sale_alcohol=true.
 * Optionally filters by region.
 */
export function useGardforsaljningProducers(region?: GardforsaljningRegion) {
  return useQuery({
    queryKey: ["gardforsaljning-producers", region?.slug ?? "all"],
    queryFn: async (): Promise<GardforsaljningProducer[]> => {
      let query = supabase
        .from("producers")
        .select("id, slug, name, city, region, county, short_description, updated_at, has_farm_shop")
        .eq("has_farm_sale_alcohol", true)
        .eq("status", "public")
        .order("name");

      if (region) {
        // Filter by region or county matching any of the dbValues
        query = query.or(
          region.dbValues.map((v) => `region.eq.${v},county.eq.${v}`).join(",")
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error("Gardforsaljning query error:", error);
        throw error;
      }

      return (data ?? []).map((p) => ({
        id: p.id,
        slug: p.slug,
        name: p.name,
        city: p.city,
        region: p.region,
        county: p.county,
        short_description: p.short_description,
        updated_at: p.updated_at,
        has_farm_shop: p.has_farm_shop ?? false,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}
